import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Maximize2, Minimize2, Pause, Play, Volume2, VolumeX } from 'lucide-react';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Spinner } from 'components/ui/spinner';
import { Question } from '../../types';
import { videos } from '../../services/api';

const HIDE_CONTROLS_DELAY_MS = 1500;

interface VideoPlayerQuestionProps {
  question: Question;
  onValidationChange?: (isValid: boolean) => void;
  initiallyValid?: boolean;
  /** Lets the learner move on when the video cannot play. Without it, a playback error only shows the message. */
  onSkip?: () => void;
  /** Reports whether the video currently cannot be played. */
  onUnavailableChange?: (isUnavailable: boolean) => void;
}

export const VideoPlayerQuestion: React.FC<VideoPlayerQuestionProps> = ({
  question,
  onValidationChange,
  initiallyValid = false,
  onSkip,
  onUnavailableChange,
}) => {
  const [reloadToken, setReloadToken] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [playbackNotice, setPlaybackNotice] = useState<string | null>(null);
  const completedRef = useRef(initiallyValid);
  const playRequestRef = useRef(0);
  const metadataVideoUrl = question.metadata?.video_url ?? null;
  const lastVolumeRef = useRef(1);
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controlsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    completedRef.current = Boolean(initiallyValid);
    onValidationChange?.(completedRef.current);
  }, [initiallyValid, onValidationChange, question.id]);

  const canPlayVideo = useMemo(() => {
    if (!videoUrl) {
      return false;
    }
    return ReactPlayer.canPlay?.(videoUrl) ?? false;
  }, [videoUrl]);

  const videoStorageKey = useMemo(() => {
    if (question.video_url) {
      return question.video_url;
    }
    if (question.media?.media_type === 'video') {
      return question.media.storage_key ?? null;
    }
    return null;
  }, [question.media?.media_type, question.media?.storage_key, question.video_url]);

  useEffect(() => {
    let cancelled = false;
    playRequestRef.current += 1;
    setIsPlaying(false);
    setHasStarted(false);
    setIsBuffering(false);
    setControlsVisible(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
    setIsLoading(true);
    setError(null);
    setPlaybackNotice(null);
    setVideoUrl(null);

    const loadVideo = async () => {
      try {
        const url = videoStorageKey
          ? await videos.getSignedUrl(videoStorageKey)
          : metadataVideoUrl;
        if (!cancelled) {
          setVideoUrl(url);
          if (!url) setError('Video URL is missing.');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load video', err);
          setError('Unable to load video. Please try again later.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadVideo();
    return () => { cancelled = true; };
  }, [videoStorageKey, metadataVideoUrl, question.id, reloadToken]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement && document.fullscreenElement === wrapperRef.current));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const clearHideControlsTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  const showControls = useCallback(() => {
    clearHideControlsTimeout();
    setControlsVisible(true);
  }, [clearHideControlsTimeout]);

  const scheduleHideControls = useCallback(() => {
    clearHideControlsTimeout();
    hideControlsTimeoutRef.current = setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      const controlsEl = controlsContainerRef.current;
      const player = playerRef.current;
      if (!player || player.paused || player.ended || (activeElement && controlsEl?.contains(activeElement))) {
        return;
      }
      setControlsVisible(false);
    }, HIDE_CONTROLS_DELAY_MS);
  }, [clearHideControlsTimeout]);

  const handleBufferStart = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handleBufferEnd = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleTogglePlayback = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const request = ++playRequestRef.current;
    setPlaybackNotice(null);
    showControls();
    if (!player.paused && !player.ended) {
      player.pause();
      return;
    }
    if (player.ended) player.currentTime = 0;
    // ReactPlayer v3 exposes the media element. Keep playback event-driven:
    // its controlled `playing` prop can restart a natively paused/ended video
    // during unrelated renders, before React has committed the pause state.
    try {
      const playRequest = player.play();
      playRequest?.catch(() => {
        if (playerRef.current !== player || playRequestRef.current !== request || !player.paused) return;
        setIsPlaying(false);
        setIsBuffering(false);
        setPlaybackNotice('Playback could not start. Select Play to try again.');
        showControls();
      });
    } catch {
      setIsPlaying(false);
      setPlaybackNotice('Playback could not start. Select Play to try again.');
    }
  }, [showControls]);

  const handleToggleMute = useCallback(() => {
    showControls();
    scheduleHideControls();
    setIsMuted((prev) => {
      if (prev) {
        const restoredVolume = lastVolumeRef.current > 0 ? lastVolumeRef.current : 0.5;
        setVolume(restoredVolume);
        return false;
      }
      lastVolumeRef.current = volume > 0 ? volume : 0.5;
      return true;
    });
  }, [scheduleHideControls, showControls, volume]);

  const handleVolumeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(event.target.value);
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
      lastVolumeRef.current = newVolume;
    }
    showControls();
    scheduleHideControls();
  }, [scheduleHideControls, showControls]);

  const handleToggleFullscreen = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Failed to exit fullscreen', err);
      });
      return;
    }

    wrapper.requestFullscreen().catch((err) => {
      console.error('Failed to enter fullscreen', err);
    });
    showControls();
    scheduleHideControls();
  }, [scheduleHideControls, showControls]);

  const handleWrapperClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target instanceof HTMLButtonElement || event.defaultPrevented) {
        return;
      }
      handleTogglePlayback();
    },
    [handleTogglePlayback],
  );

  const handleComplete = useCallback(() => {
    setIsPlaying(false);
    handleBufferEnd();
    completedRef.current = true;
    onValidationChange?.(true);
    showControls();
  }, [handleBufferEnd, onValidationChange, showControls]);

  const handleWrapperMouseEnter = useCallback(() => {
    showControls();
    scheduleHideControls();
  }, [scheduleHideControls, showControls]);

  const handleWrapperMouseMove = useCallback(() => {
    showControls();
    scheduleHideControls();
  }, [scheduleHideControls, showControls]);

  const handleWrapperMouseLeave = useCallback(() => {
    scheduleHideControls();
  }, [scheduleHideControls]);

  useEffect(() => {
    if (isPlaying) {
      scheduleHideControls();
    } else {
      showControls();
    }
  }, [isPlaying, scheduleHideControls, showControls]);

  useEffect(() => {
    return () => {
      clearHideControlsTimeout();
    };
  }, [clearHideControlsTimeout]);

  const handlePlaybackError = useCallback((playbackError: unknown) => {
    console.error('Video playback error', playbackError);
    setIsPlaying(false);
    setIsBuffering(false);
    setError('Unable to load video. Please try again later.');
    onValidationChange?.(completedRef.current);
    showControls();
  }, [onValidationChange, showControls]);

  const unsupportedVideo = !isLoading && Boolean(videoUrl) && !canPlayVideo;
  const shouldRenderPlayer = !isLoading && Boolean(videoUrl) && canPlayVideo && !error;
  const isUnavailable = Boolean(error) || unsupportedVideo;

  useEffect(() => {
    onUnavailableChange?.(isUnavailable);
  }, [isUnavailable, onUnavailableChange]);

  return (
    <div>
      {isUnavailable && (
        <Alert variant="destructive">
          <AlertDescription>{error ?? 'This video format is not supported.'}</AlertDescription>
          {onSkip ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {error ? (
                <Button type="button" size="auto" variant="outline" onClick={() => setReloadToken((token) => token + 1)}>
                  Try again
                </Button>
              ) : null}
              <Button type="button" size="auto" onClick={onSkip}>
                Continue without it
              </Button>
            </div>
          ) : null}
        </Alert>
      )}

      {playbackNotice && <p role="status">{playbackNotice}</p>}

      {isLoading && (
        <div className="flex h-52 items-center justify-center">
          <Spinner />
        </div>
      )}

      {shouldRenderPlayer && videoUrl && (
        <div
          ref={wrapperRef}
          role="group"
          aria-label="Video player"
          className={`relative mx-auto flex w-full items-center justify-center overflow-hidden rounded-2xl bg-black ${
            isFullscreen ? 'h-screen max-h-none' : 'max-h-[43.75rem] max-w-[56.25rem]'
          }`}
          style={isFullscreen ? undefined : { maxHeight: 800 }}
          onClick={handleWrapperClick}
          onMouseEnter={handleWrapperMouseEnter}
          onMouseMove={handleWrapperMouseMove}
          onMouseLeave={handleWrapperMouseLeave}
        >
          <ReactPlayer
            ref={playerRef}
            src={videoUrl ?? undefined}
            playsInline
            controls={false}
            volume={volume}
            muted={isMuted}
            width="100%"
            height="100%"
            style={
              isFullscreen
                ? { width: '100%', height: '100%' }
                : { width: '100%', height: '100%', maxHeight: '700px', maxWidth: '100%' }
            }
            className="rounded-2xl"
            onEnded={handleComplete}
            onError={handlePlaybackError}
            onReady={handleBufferEnd}
            onCanPlay={handleBufferEnd}
            onCanPlayThrough={handleBufferEnd}
            onPlaying={handleBufferEnd}
            onVolumeChange={(event) => {
              const player = event.currentTarget;
              setVolume(player.volume);
              setIsMuted(player.muted);
              if (player.volume > 0) lastVolumeRef.current = player.volume;
            }}
            onWaiting={handleBufferStart}
            onStalled={handleBufferStart}
            onPlay={() => {
              playRequestRef.current += 1;
              setIsPlaying(true);
              setHasStarted(true);
              handleBufferEnd();
              showControls();
              scheduleHideControls();
            }}
            onPause={() => {
              playRequestRef.current += 1;
              setIsPlaying(false);
              handleBufferEnd();
              showControls();
            }}
          />

          {isBuffering && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40">
              <Spinner />
            </div>
          )}

          {!hasStarted && !isBuffering && (
            <button
              type="button"
              className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full bg-black/60 transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-black/60"
              aria-label="Play video"
              onClick={(event) => {
                event.stopPropagation();
                handleTogglePlayback();
              }}
            >
              <Play className="h-8 w-8 text-white" />
            </button>
          )}

          <div
            ref={controlsContainerRef}
            role="group"
            aria-label="Video controls"
            className={`absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-3 rounded-full bg-black/70 px-4 py-3 text-sm text-white transition-opacity duration-200 ${
              controlsVisible ? 'opacity-100 pointer-events-auto' : 'pointer-events-none opacity-0'
            }`}
            onClick={(event) => event.stopPropagation()}
            onFocusCapture={() => {
              showControls();
              scheduleHideControls();
            }}
            onBlurCapture={(event) => {
              const relatedTarget = event.relatedTarget as Node | null;
              if (!event.currentTarget.contains(relatedTarget)) {
                scheduleHideControls();
              }
            }}
          >
            <button
              type="button"
              onClick={handleTogglePlayback}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-black/60"
              aria-pressed={isPlaying}
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={handleToggleMute}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-black/60"
              aria-pressed={isMuted}
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>

            <label className="flex items-center gap-2 font-medium text-white">
              <span className="sr-only">Volume</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className="h-1 w-28 cursor-pointer accent-white"
                aria-label="Video volume"
              />
            </label>

            <div className="ml-auto">
              <button
                type="button"
                onClick={handleToggleFullscreen}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-black/60"
                aria-pressed={isFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

